import { ComposableTable, Tooltip, SingleSelectDropdown } from '@abtasty/design-system';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSegmentTrack } from '@application/hooks/useSegment/useSegment';
import { CellTwoLines } from '@src/application/modules/shared/components/CellTwoLines/CellTwoLines';
import styles from '@src/application/modules/shared/styles/CampaignSummary/CampaignSummary.module.scss';
import { FsCampaign, FsCampaignData, FsVariation, FsVariationToForce } from '@src/application/modules/flagship/type';
import { useCallback, useMemo } from 'react';
import { CampaignType, ColumnHeaders, FlagshipCampaignColumn } from '@src/application/modules/shared/type';
import { Desktop, Mobile } from '@src/application/modules/shared/components/MediaQuery/MediaQuery';
import { useNavigate } from 'react-router-dom';
import { TestIcon } from '../TestIcon/TestIcon';
import { TargetingsComponent } from './TargetingsComponent';
import { WarningWrapper } from './Warning/Warning';
import { ExpandIcon } from '@src/application/modules/shared/components/ExpandIcon';

const { Cell, Row } = ComposableTable;

type CampaignSummaryProps = {
  test: FsCampaign;
  expand: () => void;
  expanded: boolean;
  header: ColumnHeaders<FlagshipCampaignColumn>;
  onVariationChanged: (variation: FsVariationToForce) => void;
  CampaignData: FsCampaignData;
  defaultVariationId?: string;
  visitorVariationId?: string;
  selectedVariation?: FsVariation | undefined;
  type: CampaignType;
};

export const CampaignSummary = ({
  test,
  expand,
  expanded,
  header,
  onVariationChanged,
  CampaignData,
  defaultVariationId,
  visitorVariationId,
  selectedVariation,
  type,
}: CampaignSummaryProps) => {
  const { t } = useTranslation();
  const segmentTrack = useSegmentTrack();
  const { targetingGroups, variations } = CampaignData;
  const CampaignDataJSON = JSON.stringify(variations);
  const navigate = useNavigate();
  const mobileDetailsLink = () => navigate(`campaigns/${test.id}/details`);

  const secondaryTitle = t('ID: {{testId}}', { testId: test.id });

  const variationsOptions = useMemo(() => {
    return variations.map((item) => ({
      label: `${item.name} | ${t('ID: {{testId}}', { testId: item.id })}`,
      value: item.id,
    }));
  }, [CampaignDataJSON]);

  const defaultVariationsOptions = useMemo(() => {
    return variationsOptions.find((x) => x.value === selectedVariation?.id);
  }, [selectedVariation?.id]);

  const onVariationSelectorChange = useCallback(
    (options: { label: string; value: string }) => {
      const foundVariation = variations.find((x) => x.id === options.value) as FsVariation;
      onVariationChanged({
        campaignId: test.id,
        campaignName: test.name,
        campaignType: test.type,
        CampaignSlug: test.slug,
        variationGroupId: foundVariation.variationGroupId as string,
        variationGroupName: foundVariation.variationGroupName,
        variation: foundVariation,
      });
      segmentTrack('Qa - Click variations dropdown');
    },
    [CampaignDataJSON]
  );

  return (
    <Row className={styles.row} dataTestId="row-campaign">
      <Desktop>
        {/* Test Type */}
        <Cell onClick={expand} width={header.type.width} className={styles.cell}>
          <TestIcon testType={test.type} dataTestId="test-icon" />
        </Cell>

        {/* Test Name */}
        <CellTwoLines
          onClick={expand}
          width={header.name.width}
          text={test.name}
          secondaryText={secondaryTitle}
          data-testid="row-campaign-name"
        />

        {/* Variation/Experience */}
        {CampaignData.variations.length ? (
          <Cell width={header.variation.width}>
            <div className={styles.variationSelect} data-testid="row-campaign-dropdown-variation">
              <SingleSelectDropdown
                isDisabled={type === 'website'}
                selectorType="outlined32"
                onChange={onVariationSelectorChange}
                value={defaultVariationsOptions}
                options={variationsOptions}
              />
            </div>
            <div className={styles.warningInfo}>
              <WarningWrapper
                visitorVariationId={visitorVariationId}
                defaultVariationId={defaultVariationId}
                selectedVariationId={selectedVariation?.id}
              />
            </div>
          </Cell>
        ) : (
          <Cell
            onClick={expand}
            width={header.variation.width}
            className={classNames(styles.select, styles.null, styles.smallValue)}
          >
            {t('Not affected to test')}
          </Cell>
        )}

        {/* Segment */}
        <Cell
          onClick={expand}
          width={header.targeting.width}
          className={classNames(styles.smallValue)}
          dataTestId="targeting"
        >
          <Tooltip
            content={<TargetingsComponent targetingGroups={targetingGroups} key="1" />}
            color="blueLight"
            placement="top-start"
          >
            <TargetingsComponent targetingGroups={targetingGroups} className={styles.hiddenTargeting} key="2" />
          </Tooltip>
        </Cell>

        {/* Expand Chevron */}
        <Cell onClick={expand} width={header.expand.width}>
          <ExpandIcon expanded={expanded} />
        </Cell>
      </Desktop>

      <Mobile>
        <Cell onClick={mobileDetailsLink} width={header.type.width} className={styles.cell}>
          <TestIcon testType={test.type} />
        </Cell>
        <CellTwoLines
          onClick={mobileDetailsLink}
          width={header.name.width}
          text={test.name}
          secondaryText={secondaryTitle}
        />
      </Mobile>
    </Row>
  );
};

export default CampaignSummary;
